import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import backendUrl from '../config';

export interface ServicePermission {
  service: string;
  role: 'admin' | 'user';
}

export interface User {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user' | 'superadmin' | 'anis-admin' | 'anis-user' | 'cp-admin' | 'cp-user';
  service_permissions?: ServicePermission[];
  is_active: boolean;
  created_at: string;
  avatar?: string;
}

interface ValidationErrors {
  [field: string]: string[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  fieldErrors: ValidationErrors | null;
  successMessage: string | null;
  isAuthenticated: boolean;
}

function setAihubTokenCookie(token: string | null) {
  if (typeof document === 'undefined') return;
  if (!token) {
    // Clear cookie
    document.cookie = "aihub_token=; Path=/; Max-Age=0; Secure; SameSite=Lax";
    return;
  }
  document.cookie = `aihub_token=${encodeURIComponent(token)}; Path=/; Max-Age=3600; Secure; SameSite=Lax`;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('aihub_token'),
  loading: !!localStorage.getItem('aihub_token'), // Start loading if token exists
  error: null,
  fieldErrors: null,
  successMessage: null,
  isAuthenticated: false,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('username', credentials.email);
      formData.append('password', credentials.password);

      const loginResponse = await axios.post(`${backendUrl}/api/auth/login`, formData);
      const access_token: string = loginResponse.data.access_token;
      setAihubTokenCookie(access_token);

      // Fetch user immediately after login to populate auth state
      const userResponse = await axios.get(`${backendUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });

      return { access_token, user: userResponse.data as User };
    } catch (error: any) {
      // Handle FastAPI validation error format (422 status with detail array)
      if (error.response?.status === 422 && Array.isArray(error.response?.data?.detail)) {
        const validationErrors = error.response.data.detail;
        const fieldErrors: ValidationErrors = {};

        // Transform FastAPI validation errors to our field error format
        validationErrors.forEach((err: any) => {
          if (err.loc && Array.isArray(err.loc) && err.loc.length >= 2) {
            const fieldName = err.loc[err.loc.length - 1]; // Get the field name (last element)
            if (!fieldErrors[fieldName]) {
              fieldErrors[fieldName] = [];
            }
            fieldErrors[fieldName].push(err.msg || 'Invalid value');
          }
        });

        return rejectWithValue({
          message: 'Please fix the errors below and try again.',
          fieldErrors
        });
      }

      // Handle structured error responses from backend
      if (error.response?.data?.detail && typeof error.response.data.detail === 'object' && !Array.isArray(error.response.data.detail)) {
        const errorDetail = error.response.data.detail;

        // If it has structured errors with field-specific messages
        if (errorDetail.errors) {
          return rejectWithValue({
            message: errorDetail.message || 'Login failed',
            fieldErrors: errorDetail.errors
          });
        }
      }

      // Handle string error messages and other error types
      let errorMessage = 'Login failed. Please try again.';
      let fieldErrors: ValidationErrors | null = null;
      
      if (error.response?.status === 400) {
        // Handle 400 errors with structured detail
        if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
          const errorDetail = error.response.data.detail;
          if (errorDetail.errors) {
            return rejectWithValue({
              message: errorDetail.message || 'Login failed',
              fieldErrors: errorDetail.errors
            });
          }
        }
        errorMessage = 'Invalid login credentials. Please check your information and try again.';
      } else if (error.response?.status === 401) {
        // Handle 401 errors with structured detail
        if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
          const errorDetail = error.response.data.detail;
          if (errorDetail.errors) {
            return rejectWithValue({
              message: errorDetail.message || 'Authentication failed',
              fieldErrors: errorDetail.errors
            });
          }
        }
        
        if (typeof error.response?.data?.detail === 'string') {
          if (error.response.data.detail === 'Incorrect email or password') {
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          } else if (error.response.data.detail === 'Could not validate credentials') {
            errorMessage = 'Authentication failed. Please try logging in again.';
          } else {
            errorMessage = 'Invalid credentials. Please check your email and password.';
          }
        } else {
          errorMessage = 'Invalid credentials. Please check your email and password.';
        }
      } else if (error.response?.status === 422) {
        errorMessage = 'Please enter a valid email address and password.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      return rejectWithValue({ message: errorMessage, fieldErrors });
    }
  }
);

export const loginWithOutlook = createAsyncThunk(
  'auth/loginWithOutlook',
  async (_, { rejectWithValue }) => {
    try {
      // This would integrate with Microsoft Graph API
      const response = await axios.post(`${backendUrl}/api/auth/outlook-login`);
      const access_token: string = response.data.access_token;
      setAihubTokenCookie(access_token);

      const userResponse = await axios.get(`${backendUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });

      return { access_token, user: userResponse.data as User };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Outlook login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: { email: string; username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${backendUrl}/api/auth/register`, userData);
      const access_token: string = response.data.access_token;

      // After register, fetch user if token provided; otherwise, return as-is
      if (access_token) {
        const userResponse = await axios.get(`${backendUrl}/api/users/me`, {
          headers: { Authorization: `Bearer ${access_token}` },
        });
        return { access_token, user: userResponse.data as User };
      }

      return response.data;
    } catch (error: any) {
      // Handle FastAPI validation error format (422 status with detail array)
      if (error.response?.status === 422 && Array.isArray(error.response?.data?.detail)) {
        const validationErrors = error.response.data.detail;
        const fieldErrors: ValidationErrors = {};

        // Transform FastAPI validation errors to our field error format
        validationErrors.forEach((err: any) => {
          if (err.loc && Array.isArray(err.loc) && err.loc.length >= 2) {
            const fieldName = err.loc[err.loc.length - 1]; // Get the field name (last element)
            if (!fieldErrors[fieldName]) {
              fieldErrors[fieldName] = [];
            }
            fieldErrors[fieldName].push(err.msg || 'Invalid value');
          }
        });

        return rejectWithValue({
          message: 'Please fix the errors below and try again.',
          fieldErrors
        });
      }

      // Handle structured error responses from backend
      if (error.response?.data?.detail && typeof error.response.data.detail === 'object' && !Array.isArray(error.response.data.detail)) {
        const errorDetail = error.response.data.detail;

        // If it has structured errors with field-specific messages
        if (errorDetail.errors) {
          return rejectWithValue({
            message: errorDetail.message || 'Registration failed',
            fieldErrors: errorDetail.errors
          });
        }
      }

      // Handle string error messages and other error types
      let errorMessage = 'Registration failed. Please try again.';
      let fieldErrors: ValidationErrors | null = null;

      if (error.response?.status === 400) {
        // Handle 400 errors with structured detail
        if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
          const errorDetail = error.response.data.detail;
          if (errorDetail.errors) {
            return rejectWithValue({
              message: errorDetail.message || 'Registration failed',
              fieldErrors: errorDetail.errors
            });
          }
        }
        
        if (typeof error.response?.data?.detail === 'string') {
          if (error.response.data.detail.includes('email')) {
            errorMessage = 'This email address is already registered. Please use a different email or try logging in.';
          } else if (error.response.data.detail.includes('username')) {
            errorMessage = 'This username is already taken. Please choose a different username.';
          } else {
            errorMessage = 'Invalid registration data. Please check your information and try again.';
          }
        } else {
          errorMessage = 'Invalid registration data. Please check your information and try again.';
        }
      } else if (error.response?.status === 422) {
        if (typeof error.response?.data?.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = 'Please check your input and try again.';
        }
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      return rejectWithValue({ message: errorMessage, fieldErrors });
    }
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initializeAuth',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('aihub_token');
      if (!token) {
        setAihubTokenCookie(null);
        return { token: null, user: null };
      }
      setAihubTokenCookie(token);

      // Validate token by fetching user data
      const response = await axios.get(`${backendUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return { token, user: response.data };
    } catch (error: any) {
      // Token is invalid or expired, clear it
      localStorage.removeItem('aihub_token');
      
      // If it's a 401 error, the token has expired
      if (error.response?.status === 401) {
        // Don't set session expired message here as it's handled by axios interceptor
        // when user actually tries to access protected resources
        return { token: null, user: null };
      }
      
      return { token: null, user: null };
    }
  }
);

export const fetchUser = createAsyncThunk(
  'auth/fetchUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: AuthState };
      if (!auth.token) throw new Error('No token');

      const response = await axios.get(`${backendUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      // Handle authentication errors gracefully
      if (error.response?.status === 401) {
        // Don't show error messages for 401s as they're handled by axios interceptor
        // The interceptor will redirect to login with appropriate message
        return rejectWithValue('Session expired');
      } else if (error.response?.status >= 500) {
        return rejectWithValue('Server error. Please try again later.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        return rejectWithValue('Network error. Please check your connection.');
      }
      
      return rejectWithValue('Failed to load user information.');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('aihub_token');
      setAihubTokenCookie(null);
    },
    clearError: (state) => {
      state.error = null;
      state.fieldErrors = null;
    },
    clearFieldErrors: (state) => {
      state.fieldErrors = null;
    },
    clearSuccess: (state) => {
      state.successMessage = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('aihub_token', action.payload);
      setAihubTokenCookie(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = null;
        state.successMessage = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.successMessage = 'Login successful! Redirecting to dashboard...';
        localStorage.setItem('aihub_token', action.payload.access_token);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        if (payload && typeof payload === 'object' && payload.fieldErrors) {
          state.error = payload.message || 'Login failed';
          state.fieldErrors = payload.fieldErrors;
        } else if (typeof payload === 'string') {
          state.error = payload;
          state.fieldErrors = null;
        } else if (payload && typeof payload === 'object') {
          // Handle unexpected object format - extract message or stringify
          state.error = payload.message || JSON.stringify(payload);
          state.fieldErrors = null;
        } else {
          state.error = 'Login failed. Please try again.';
          state.fieldErrors = null;
        }
      })
      // Outlook Login
      .addCase(loginWithOutlook.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithOutlook.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.access_token;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        localStorage.setItem('aihub_token', action.payload.access_token);
      })
      .addCase(loginWithOutlook.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fieldErrors = null;
        state.successMessage = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        // If register returns token+user (auto-login), set them
        if ((action.payload as any)?.access_token) {
          state.token = (action.payload as any).access_token;
          state.user = (action.payload as any).user;
          state.isAuthenticated = true;
          state.successMessage = 'Registration successful! Redirecting to dashboard...';
          localStorage.setItem('aihub_token', (action.payload as any).access_token);
        } else {
          state.user = action.payload as any;
          state.successMessage = 'Registration successful! Redirecting to login...';
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        if (payload && typeof payload === 'object' && payload.fieldErrors) {
          state.error = payload.message;
          state.fieldErrors = payload.fieldErrors;
        } else {
          state.error = payload as string;
          state.fieldErrors = null;
        }
      })
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.token && action.payload.user) {
          state.token = action.payload.token;
          state.user = action.payload.user;
          state.isAuthenticated = true;
        } else {
          state.token = null;
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Fetch User
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });
  },
});

export const { logout, clearError, clearFieldErrors, clearSuccess, setToken } = authSlice.actions;
export default authSlice.reducer;
